import { Inject, Injectable, InjectionToken, OnDestroy, Optional } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Params } from '@angular/router';

import { Subscription, combineLatest, map, mergeMap } from 'rxjs';

import { RouteData, RouterPageTitle } from '@hdms/shared/common-types';

import { EnvironmentService } from '../environment/environment.service';
import { PlatformService } from '../platform/platform.service';
import { RouterHelperService } from '../router/router-helper.service';
import { TRANSLATE_FN, TranslateFn } from '../translation/translate.token';

export const COMMON_PAGE_TITLE = new InjectionToken<string>('util-core.commonPageTitle');

const TITLE_DELIMITER = ' - ';

@Injectable({
  providedIn: 'root'
})
export class RouterPageTitleService implements OnDestroy {
  private appTitle: string;
  private readonly routeSubscription: Subscription;

  constructor(
    private readonly routerHelper: RouterHelperService,
    private readonly titleService: Title,
    private readonly envService: EnvironmentService,
    private readonly platform: PlatformService,
    @Optional() @Inject(TRANSLATE_FN) private readonly translate: TranslateFn,
    @Optional() @Inject(COMMON_PAGE_TITLE) pageTitle: string
  ) {
    if (translate) {
      this.setAppTitle(pageTitle);
      this.routeSubscription = this.subscribeToRouterEvents();
    }
  }

  // will be executed in SSR env
  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
  }

  private subscribeToRouterEvents(): Subscription {
    return this.routerHelper.activatedRoute$
      .pipe(
        mergeMap(route => combineLatest([route.data, route.params])),
        map(([data, params]) => this.generateRouterPageTitle(data, params))
      )
      .subscribe((title: RouterPageTitle) => this.setPageTitle(title));
  }

  private generateRouterPageTitle({ pageTitle, i18nextNamespaces }: RouteData, params: Params): RouterPageTitle {
    return {
      key: this.appTitle,
      ns: i18nextNamespaces,
      ...pageTitle,
      paramValue: params[pageTitle?.paramKey]
    };
  }

  private setPageTitle({ key, ns, pattern, paramKey, paramValue }: RouterPageTitle): void {
    let pageTitle = this.appTitle;
    if (key !== this.appTitle) {
      pageTitle = this.translate(key, { ns });

      if (pattern) {
        pageTitle = this.translate(pattern, {
          ns,
          title: pageTitle,
          [paramKey]: paramValue
        });
      }

      pageTitle = `${pageTitle}${TITLE_DELIMITER}${this.getAppTitle()}`;
    }
    this.titleService.setTitle(pageTitle);
  }

  /**
   * For compatibility reasons in case page title has already been fully set by Hybris,
   * we only take the last part which is the generic page title fragment.
   * The approach is based on the convention having generic fragment separated by " - ".
   * @private
   */
  private getAppTitle(): string {
    if (this.appTitle.includes(TITLE_DELIMITER)) {
      const titleParts = this.appTitle.split(TITLE_DELIMITER);
      return titleParts[titleParts.length - 1];
    }
    return this.appTitle;
  }

  /**
   * in case app runs standalone (outside Hybris) on the server,
   * set the initial title provided via translation injection token,
   * otherwise get existing title from DOM.
   * @param pageTitle
   * @private
   */
  private setAppTitle(pageTitle: string): void {
    const { isStandalone } = this.envService;
    this.appTitle = isStandalone ? pageTitle : this.titleService.getTitle();
    isStandalone && this.platform.isServer && this.titleService.setTitle(pageTitle);
  }
}
